import { useEffect, useRef, useCallback } from 'react';

/**
 * Tipos de mensagens que podem ser transmitidas entre abas
 */
export type BroadcastMessageType =
  | 'AUTH_SIGNED_OUT'      // Usuário fez logout em uma aba
  | 'AUTH_SIGNED_IN'       // Usuário fez login em uma aba
  | 'AUTH_SESSION_EXPIRED' // Sessão expirou por timeout
  | 'ACTIVITY_UPDATE'      // Atualização de atividade do usuário
  | 'DATE_RANGE_CHANGED'   // Período de datas alterado
  | 'DATA_REFRESH'         // Dados foram atualizados
  | 'TAB_VISIBLE'          // Aba ficou visível
  | 'TAB_HIDDEN'           // Aba ficou oculta
  | 'LAST_SEEN_UPDATED';   // last_seen_at foi atualizado no banco

export interface BroadcastMessage<T = unknown> {
  type: BroadcastMessageType;
  payload?: T;
  timestamp: number;
  tabId: string;
}

// ID único para esta aba
const TAB_ID = `tab_${Date.now()}_${Math.random().toString(36).substring(2, 9)}`;

// Nome do canal de comunicação
const CHANNEL_NAME = 'd12-hub-sync';

// Chave do localStorage para fallback
const STORAGE_KEY = 'd12-hub-broadcast';

/**
 * Hook para comunicação entre abas usando BroadcastChannel API
 * com fallback para localStorage para navegadores mais antigos
 *
 * @param onMessage - Callback chamado quando uma mensagem é recebida de outra aba
 * @returns Função para enviar mensagens para outras abas
 */
export function useBroadcastChannel(
  onMessage?: (message: BroadcastMessage) => void
) {
  const channelRef = useRef<BroadcastChannel | null>(null);
  const onMessageRef = useRef(onMessage);

  // Manter referência atualizada do callback
  useEffect(() => {
    onMessageRef.current = onMessage;
  }, [onMessage]);

  // Função para enviar mensagem
  const postMessage = useCallback(<T = unknown>(
    type: BroadcastMessageType,
    payload?: T
  ) => {
    const message: BroadcastMessage<T> = {
      type,
      payload,
      timestamp: Date.now(),
      tabId: TAB_ID,
    };

    // Tentar usar BroadcastChannel primeiro
    if (channelRef.current) {
      try {
        channelRef.current.postMessage(message);
      } catch (error) {
        console.warn('[BroadcastChannel] Error posting message:', error);
      }
    }

    // Também usar localStorage como fallback/backup
    // Isso garante compatibilidade com navegadores mais antigos
    try {
      localStorage.setItem(STORAGE_KEY, JSON.stringify(message));
      // Remover após um curto delay para permitir que outras abas leiam
      setTimeout(() => {
        try {
          localStorage.removeItem(STORAGE_KEY);
        } catch {
          // Ignorar erros de remoção
        }
      }, 100);
    } catch (error) {
      console.warn('[BroadcastChannel] Error with localStorage fallback:', error);
    }
  }, []);

  // Inicializar canal e listeners
  useEffect(() => {
    // Handler para mensagens do BroadcastChannel
    const handleChannelMessage = (event: MessageEvent<BroadcastMessage>) => {
      const message = event.data;

      // Ignorar mensagens da própria aba
      if (message.tabId === TAB_ID) return;

      // Chamar callback se definido
      if (onMessageRef.current) {
        onMessageRef.current(message);
      }
    };

    // Handler para eventos de storage (fallback)
    const handleStorageEvent = (event: StorageEvent) => {
      if (event.key !== STORAGE_KEY || !event.newValue) return;

      try {
        const message = JSON.parse(event.newValue) as BroadcastMessage;

        // Ignorar mensagens da própria aba
        if (message.tabId === TAB_ID) return;

        // Chamar callback se definido
        if (onMessageRef.current) {
          onMessageRef.current(message);
        }
      } catch (error) {
        console.warn('[BroadcastChannel] Error parsing storage event:', error);
      }
    };

    // Tentar criar BroadcastChannel
    if (typeof BroadcastChannel !== 'undefined') {
      try {
        channelRef.current = new BroadcastChannel(CHANNEL_NAME);
        channelRef.current.onmessage = handleChannelMessage;
      } catch (error) {
        console.warn('[BroadcastChannel] Error creating channel:', error);
      }
    }

    // Sempre adicionar listener de storage como fallback
    window.addEventListener('storage', handleStorageEvent);

    // Cleanup
    return () => {
      if (channelRef.current) {
        channelRef.current.close();
        channelRef.current = null;
      }
      window.removeEventListener('storage', handleStorageEvent);
    };
  }, []);

  return {
    postMessage,
    tabId: TAB_ID,
  };
}

/**
 * Retorna o ID único desta aba
 */
export function getTabId(): string {
  return TAB_ID;
}
